/**
 * Names the server adds to every tool's input schema. Not arguments any tool
 * declared, and not the model's answer to a question the user asked.
 *
 * Mirrors `COMPRESSION_INTENT_FIELD` in
 * `packages/roboto_service/src/roboto_service/ai/core/tool_decls.py`.
 * The server strips these before dispatching a server-side tool and the SDK
 * strips them before invoking a client-side callback; they survive on the
 * persisted turn because the compression engine reads them back later. This
 * app is the one reader that renders `input` verbatim, so it is the one place
 * they can reach a user.
 */
const BACKEND_INJECTED_INPUT_FIELDS: readonly string[] = [
  "_compression_intent",
];

/**
 * The model is free to emit any JSON as a tool's input, so a value typed as
 * the tool's parameter object is not necessarily one.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * `input` without the server's meta-fields, for display.
 *
 * Returns a new object; the caller's copy is left alone, since the unfiltered
 * input is still what gets dispatched. Filtering is shallow: these fields are
 * only ever injected at the top level of a tool's input schema.
 *
 * A non-object input is passed through untouched. Rebuilding one would turn a
 * malformed proposal into a well-formed-looking object — an array becoming
 * `{0: ...}` — and a preview that should have refused it would render.
 *
 * `null` is the exception, and reads as no arguments. Every caller treats the
 * return as an object — `Object.keys(params)`, `params.route` — and both throw
 * on `null` rather than rendering the malformed proposal the pass-through
 * exists to preserve.
 */
export function toolInputForDisplay(
  input: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (input === undefined || input === null) {
    return {};
  }

  if (!isPlainObject(input)) {
    return input;
  }

  return Object.fromEntries(
    Object.entries(input).filter(
      ([key]) => !BACKEND_INJECTED_INPUT_FIELDS.includes(key),
    ),
  );
}
